import { ChangeDetectionStrategy, Component, OnInit, computed, inject, input, output, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatDatepicker, MatDatepickerInput, MatDatepickerToggle } from '@angular/material/datepicker';
import { MatError, MatFormField, MatHint, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatOption, MatSelect } from '@angular/material/select';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import dayjs from 'dayjs';
import { map, of, switchMap, tap } from 'rxjs';
import { ClientRoleEnum } from 'src/app/enums/client-role.enum';
import { CompanyProductUserMemberRoleEnum } from 'src/app/enums/company-product-user-member-role.enum';
import { RoleSlugEnum } from 'src/app/enums/role-slug.enum';
import { ArrayResponse } from 'src/app/models/array_response.interface';
import { Company } from 'src/app/models/company.model';
import { DepartmentCategory } from 'src/app/models/department_category.model';
import { Role } from 'src/app/models/role.model';
import { User } from 'src/app/models/user.model';
import { ApiService } from 'src/app/services/api.service';
import { PermissionsService } from 'src/app/services/permissions.service';
import { RegexService } from 'src/app/services/regex.service';
import Countries from 'src/assets/json/countries.json';
import { ChangePasswordDialogComponent } from '../change-password-dialog/change-password-dialog.component';

@Component({
  selector: 'app-user-detail-personal-section',
  templateUrl: './user-detail-personal-section.component.html',
  styleUrl: './user-detail-personal-section.component.css',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ReactiveFormsModule,
    MatAutocomplete,
    MatAutocompleteTrigger,
    MatButton,
    MatDatepicker,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatError,
    MatFormField,
    MatHint,
    MatIcon,
    MatIconButton,
    MatInput,
    MatLabel,
    MatOption,
    MatSelect,
    MatSuffix,
  ],
})
export class UserDetailPersonalSectionComponent implements OnInit {
  private readonly api = inject(ApiService);
  private readonly dialog = inject(MatDialog);
  private readonly fb = inject(UntypedFormBuilder);
  private readonly permissions = inject(PermissionsService);
  private readonly regex = inject(RegexService);
  private readonly router = inject(Router);
  private readonly regex_patterns = this.regex.getRegex();
  private readonly company_id = signal<number | null>(null);
  private readonly selected_customer = signal<Company | null>(null);

  readonly user_id = input.required<string>();
  readonly user = input.required<User>();
  readonly its_me = input.required<boolean>();
  readonly user_updated = output<User>();

  readonly user_form: UntypedFormGroup = this.fb.group({
    name: ['', Validators.compose([Validators.required])],
    surnames: ['', Validators.compose([Validators.required])],
    email: ['', Validators.compose([Validators.required, Validators.email])],
    sex: ['', Validators.compose([Validators.required])],
    birthday: ['', Validators.compose([Validators.required])],
    phone: ['', Validators.compose([Validators.required, Validators.pattern(this.regex_patterns['phone'])])],
    role_id: ['', Validators.compose([Validators.required])],
    company_id: [''],
    github_username: ['', Validators.compose([])],
  });
  private readonly user_form_status = toSignal(this.user_form.statusChanges, { initialValue: this.user_form.status });
  private readonly github_username_control = this.user_form.get('github_username') as UntypedFormControl;
  private readonly github_username_status = toSignal(this.github_username_control.statusChanges, {
    initialValue: this.github_username_control.status,
  });
  private readonly github_username_touch_tick = signal(0);
  readonly company_form: UntypedFormGroup = this.fb.group({
    fact_name: ['', Validators.compose([Validators.required])],
    email: ['', Validators.compose([Validators.required])],
    phone: ['', Validators.compose([Validators.required])],
    fact_address: '',
    fact_city: '',
    fact_zipcode: '',
    fact_county: '',
    fact_country: '',
    vat: ['', Validators.compose([Validators.required])],
  });
  private readonly company_form_status = toSignal(this.company_form.statusChanges, { initialValue: this.company_form.status });
  readonly client_select_control = new UntypedFormControl();
  readonly countries: Country[] = Countries;
  readonly roles = toSignal(
    this.permissions.rolesObservable.pipe(
      map((roles_by_key: Record<string, Role> | null): Role[] =>
        roles_by_key == null ? [] : Object.keys(roles_by_key).map((role_key: string) => roles_by_key[role_key])
      )
    ),
    { initialValue: [] }
  );
  readonly available_customers = toSignal(
    this.client_select_control.valueChanges.pipe(
      map((search_value: Company | string | null): string => (typeof search_value === 'string' ? search_value : '')),
      switchMap((search_string: string) =>
        search_string.length >= 3
          ? this.api.getCompanies({ search: search_string }).pipe(map((response: ArrayResponse<Company>): Company[] => response.data))
          : of([])
      )
    ),
    { initialValue: [] }
  );
  readonly selected_role_id = toSignal(
    this.user_form.controls['role_id'].valueChanges.pipe(
      map((role_id: number | null): number | null => role_id),
      tap((): void => this.update_github_username_validation())
    ),
    { initialValue: null }
  );
  private readonly company_request = toSignal(
    toObservable(this.company_id).pipe(
      switchMap((company_id: number | null) => (company_id == null ? of(null) : this.api.getCompany(company_id.toString()))),
      tap((company: Company | null): void => this.apply_selected_company(company))
    ),
    { initialValue: null }
  );
  readonly customer_selected = computed((): Company | null => {
    const selected_customer = this.selected_customer();

    if (selected_customer != null) {
      return selected_customer;
    }

    return this.company_id() == null ? null : this.company_request();
  });
  readonly selected_role_slug = computed((): string | null => {
    const role_id = this.selected_role_id();
    const role_from_form = this.roles().find((role: Role) => role.id === role_id)?.slug;

    return role_from_form ?? this.user().role?.slug ?? null;
  });
  readonly is_partner_role = computed((): boolean => this.selected_role_slug() === RoleSlugEnum.PARTNER);
  readonly is_client_or_partner_role = computed((): boolean => {
    const role_slug = this.selected_role_slug();

    return role_slug === RoleSlugEnum.CLIENT || role_slug === RoleSlugEnum.PARTNER;
  });
  readonly is_users_route = computed((): boolean => this.router.url.includes('/users'));
  readonly can_manage_users = computed((): boolean => this.permissions.validateDepartment(['direction', 'rrhh']));
  readonly can_manage_roles = computed((): boolean => this.permissions.isDepartment('direction') || this.permissions.isDepartment('rrhh'));
  readonly can_edit = computed((): boolean => this.its_me() || this.can_manage_users());
  readonly can_delete_user = computed((): boolean => this.can_manage_users() && !this.its_me());
  readonly client_select_disabled = computed((): boolean => !this.can_manage_users() || this.its_me());
  readonly company_label = computed((): string => (this.is_partner_role() ? 'Empresa colaboradora' : 'Empresa'));
  readonly show_company_form = computed(
    (): boolean => this.user().role.slug === RoleSlugEnum.CLIENT && this.user().client_role === ClientRoleEnum.BUSINESS
  );
  readonly show_customer_reset = computed((): boolean => this.customer_selected() !== null);
  readonly password_button_label = computed((): string => (this.its_me() ? 'Cambiar contraseña' : 'Regenerar contraseña'));
  readonly password_button_disabled = computed((): boolean => !this.can_edit());
  readonly save_user_disabled = computed((): boolean => {
    this.user_form_status();

    return !this.user_form.valid || (this.is_users_route() && !this.can_edit());
  });
  readonly save_company_disabled = computed((): boolean => {
    this.company_form_status();

    return !this.company_form.valid;
  });
  readonly github_username_has_required_error = computed((): boolean => {
    this.github_username_status();
    this.github_username_touch_tick();

    return this.github_username_control.hasError('required') && this.github_username_control.touched;
  });

  ngOnInit(): void {
    this.user_form.controls['email'].disable();
    this.process_user_data(this.user());
  }

  display_company(company: Company | string | null): string {
    if (typeof company === 'string' || company == null) {
      return company ?? '';
    }

    return company ? company.fact_name : '';
  }

  save_user(): void {
    const body: Record<string, unknown> = {
      name: this.user_form.value.name,
      surnames: this.user_form.value.surnames,
      sex: this.user_form.value.sex,
      birthday: this.user_form.value.birthday != null ? dayjs(this.user_form.value.birthday).format('YYYY-MM-DD') : null,
      phone: this.user_form.value.phone,
      role_id: this.user_form.value.role_id,
      company_id: this.user_form.value.company_id,
      github_username: this.user_form.value.github_username,
    };

    this.api.updateUser(this.user_id(), body).subscribe((updated_user: User): void => {
      this.user_updated.emit(updated_user);
    });
  }

  save_company(): void {
    const customer_selected = this.customer_selected();

    if (customer_selected == null) {
      return;
    }

    this.api.updateCompany(customer_selected.id.toString(), this.company_form.value).subscribe((): void => {});
  }

  delete_user(): void {
    if (confirm('¿Estás seguro que quieres eliminar este usuario?')) {
      this.api.deleteUser(+this.user_id()).subscribe((): void => {
        this.router.navigate(['/employees', 'direction', 'users']);
      });
    }
  }

  change_password(): void {
    this.dialog
      .open(ChangePasswordDialogComponent, {
        width: '450px',
      })
      .afterClosed()
      .subscribe((password: string): void => {
        if (password != null) {
          this.api.changeUserPassword(this.user_id(), password).subscribe((): void => {});
        }
      });
  }

  select_customer(company: Company): void {
    this.selected_customer.set(company);
    this.user_form.patchValue({ company_id: company.id });
    this.company_form.patchValue(company);
  }

  reset_customer(): void {
    this.selected_customer.set(null);
    this.company_id.set(null);
    this.client_select_control.setValue('');
    this.user_form.patchValue({ company_id: null });
    this.company_form.reset();
  }

  update_github_username_error_state(): void {
    this.github_username_touch_tick.update((tick: number): number => tick + 1);
  }

  private update_github_username_validation(): void {
    const is_worker = this.user().role?.slug === RoleSlugEnum.WORKER;
    const is_developer =
      this.user().current_working_contract?.current_working_contract_variable_condition?.department_categories?.some(
        (department_category: DepartmentCategory): boolean =>
          department_category.company_product_user_member_role?.slug === CompanyProductUserMemberRoleEnum.DEVELOPER
      ) ?? false;

    if (is_worker && is_developer) {
      this.github_username_control.setValidators([Validators.required]);
    } else {
      this.github_username_control.clearValidators();
    }

    this.github_username_control.updateValueAndValidity();
    this.update_github_username_error_state();
  }

  private process_user_data(user: User): void {
    this.user_form.patchValue({
      name: user.name,
      surnames: user.surnames,
      email: user.email,
      sex: user.sex,
      birthday: user.birthday,
      phone: user.phone,
      role_id: user.role_id,
      company_id: user.company_id,
      github_username: user.github_username || '',
    });

    this.update_github_username_validation();
    this.company_id.set(user.company_id ?? null);
  }

  private apply_selected_company(company: Company | null): void {
    if (company == null) {
      this.company_form.reset();
      return;
    }

    this.client_select_control.setValue(company, { emitEvent: false });
    this.company_form.patchValue(company);
  }
}

interface Country {
  name: string;
  alpha_2: string;
  'country-code': string;
}
